import type { EkycUsageRow } from '../../../../api/admin/ekyc.api';

export interface EkycUsageMonth {
  month: string; // yyyy-MM
  docOk: number;
  docFail: number;
  duplicate: number;
  faceOk: number;
  faceFail: number;
  providerError: number;
  paidTotal: number;
}

const DOC = 'verify-document';
const FACE = 'liveness-check';

/** Các tháng trong cửa sổ báo cáo theo giờ Việt Nam, tháng mới nhất trước (khớp cách máy chủ gom tháng). */
export const monthKeysVn = (now: Date, months: number): string[] => {
  const vn = new Date(now.getTime() + 7 * 3600 * 1000);
  const y = vn.getUTCFullYear();
  const m = vn.getUTCMonth();
  const keys: string[] = [];
  for (let i = 0; i < Math.max(1, months); i += 1) {
    const d = new Date(Date.UTC(y, m - i, 1));
    keys.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return keys;
};

const empty = (month: string): EkycUsageMonth => ({
  month,
  docOk: 0,
  docFail: 0,
  duplicate: 0,
  faceOk: 0,
  faceFail: 0,
  providerError: 0,
  paidTotal: 0,
});

/**
 * Gom các dòng (tháng, loại gọi, kết quả, số lượt) thành một dòng mỗi tháng.
 * Tổng lượt trả phí = mọi lần đọc giấy tờ đã gửi tới nhà cung cấp (thành công, hỏng, trùng căn cước, lỗi)
 * cộng mọi lần kiểm tra khuôn mặt đã gửi tới nhà cung cấp (thành công, hỏng, lỗi).
 * Trùng căn cước ở bước khuôn mặt chỉ là kiểm tra trong cơ sở dữ liệu nên không tính phí.
 * Tháng không có dòng nào vẫn hiện với số 0 nếu nằm trong `monthKeys`.
 */
export const summarizeEkycUsage = (rows: EkycUsageRow[], monthKeys: string[] = []): EkycUsageMonth[] => {
  const byMonth = new Map<string, EkycUsageMonth>();
  monthKeys.forEach((k) => byMonth.set(k, empty(k)));
  rows.forEach((r) => {
    if (!r?.month) return;
    const row = byMonth.get(r.month) ?? empty(r.month);
    byMonth.set(r.month, row);
    const n = Number(r.count) || 0;
    if (r.action === DOC) {
      if (r.result === 'ok') row.docOk += n;
      else if (r.result === 'fail') row.docFail += n;
      else if (r.result === 'duplicate') row.duplicate += n;
      else if (r.result === 'error') row.providerError += n;
      else return;
      row.paidTotal += n;
    } else if (r.action === FACE) {
      if (r.result === 'ok') row.faceOk += n;
      else if (r.result === 'fail') row.faceFail += n;
      else if (r.result === 'duplicate') {
        row.duplicate += n;
        return; // kiểm tra trong hệ thống, không gọi nhà cung cấp
      } else if (r.result === 'error') row.providerError += n;
      else return;
      row.paidTotal += n;
    }
  });
  return Array.from(byMonth.values()).sort((a, b) => (a.month < b.month ? 1 : a.month > b.month ? -1 : 0));
};

/** "2026-11" thành "11/2026". */
export const formatMonth = (key: string): string => {
  const [y, m] = key.split('-');
  return y && m ? `${m}/${y}` : key;
};
